import ReactECharts from "echarts-for-react";

export default function CagrComparisonChart({ periods, series, height = "210px" }) {
  const allVals = series.flatMap(s => s.values).filter(v => v != null);
  if (allVals.length === 0) {
    return (
      <div className="bg-gray-900/60 border border-gray-700/50 rounded-lg p-4">
        <span className="text-gray-100 text-xs block mb-1 font-semibold tracking-wider">CAGR Comparison</span>
        <p className="text-xs text-gray-500 mt-4">No data available</p>
      </div>
    );
  }

  const minV = Math.min(...allVals);
  const maxV = Math.max(...allVals);
  const pad = Math.max((maxV - minV) * 0.35, 4);

  const makeSeries = (name, vals, color) => ({
    name,
    type: 'line',
    smooth: true,
    data: vals.map(v => v ?? null),
    connectNulls: false,
    lineStyle: { color, width: 2.5 },
    itemStyle: { color, borderColor: color, borderWidth: 2 },
    symbol: 'circle',
    symbolSize: 10,
    label: { show: false },
    emphasis: { focus: 'series' },
  });

  const option = {
    backgroundColor: 'transparent',
    animation: true,
    grid: { top: 28, right: 16, bottom: 20, left: 32, containLabel: false },
    xAxis: {
      type: 'category',
      data: periods,
      boundaryGap: true,
      axisLabel: { color: '#9ca3af', fontSize: 9, fontWeight: '600' },
      axisLine: { lineStyle: { color: '#374151' } },
      axisTick: { show: false },
      splitLine: { show: true, lineStyle: { color: '#374151', type: 'solid' } },
    },
    yAxis: {
      type: 'value',
      min: minV - pad,
      max: maxV + pad,
      axisLabel: {
        color: '#9ca3af', fontSize: 10,
        formatter: v => `${Math.round(v)}%`,
      },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: true, lineStyle: { color: '#1f2937', type: 'dashed' } },
    },
    legend: {
      top: 0,
      right: 0,
      textStyle: { color: '#d1d5db', fontSize: 9 },
      itemWidth: 12, itemHeight: 3,
      icon: 'rect',
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#111827',
      borderColor: '#374151',
      textStyle: { color: '#e5e7eb', fontSize: 11 },
      formatter: params => {
        const period = periods[params[0]?.dataIndex] || '';
        return `<b>${period}</b><br/>` + params
          .filter(p => p.value != null)
          .map(p => `<span style="color:${p.color}">●</span> ${p.seriesName}: <b>${Number(p.value).toFixed(2)}%</b>`)
          .join('<br/>');
      },
    },
    series: series.map(s => makeSeries(s.name, s.values, s.color)),
  };

  return (
    <div className="bg-gray-900/60 border border-gray-700/50 rounded-lg p-4">
      <span className="text-gray-100 text-xs block mb-1 font-semibold tracking-wider">CAGR Comparison</span>
      <ReactECharts style={{ height, width: '100%' }} option={option} />
    </div>
  );
}
